// src/pages/Login.tsx - COMPLETE WITH EMAIL/PASSWORD FOR CITIZENS
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, 
  User, 
  Smartphone, 
  Lock, 
  Mail, 
  Phone, 
  UserPlus,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Loader2,
  Home,
  Building,
  Map,
  MapPin,
  Globe,
  Calendar,
  ShieldCheck,
  RefreshCw,
  Key,
  ChevronLeft,
  Eye,
  EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';

const COMMUNITY_IMAGE_URL = "1000184364.jpg";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { 
    login, 
    loginAspirant, 
    loginCitizenWithEmailPassword,
    citizenForgotPassword,
    citizenResetPassword,
    sendOtp, 
    citizenSignup 
  } = useAuth();
  
  const [activeTab, setActiveTab] = useState<'admin' | 'aspirant' | 'citizen'>('admin');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [citizenStep, setCitizenStep] = useState<'login' | 'register' | 'forgot-password'>('login');
  const [countdown, setCountdown] = useState(0);
  
  // Admin login state - placeholders removed
  const [adminCredentials, setAdminCredentials] = useState({
    email: '',
    password: '',
  });
  
  // Aspirant login state (retains OTP)
  const [aspirantCredentials, setAspirantCredentials] = useState({
    phoneNumber: '',
    otp: '',
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpAttempts, setOtpAttempts] = useState(0);
  
  // Citizen login state (email/password)
  const [citizenLoginData, setCitizenLoginData] = useState({
    email: '',
    password: '',
  });
  
  // Citizen forgot password state
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: '',
  });
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const [resetOtp, setResetOtp] = useState('');
  const [resetOtpFields, setResetOtpFields] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  
  // Citizen registration state
  const [citizenRegisterData, setCitizenRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phoneNumber: '',
    idNumber: '',
    gender: '',
    dateOfBirth: '',
    countyId: '',
    constituencyId: '',
    wardId: '',
    physicalAddress: '',
    voterRegistrationNumber: '',
    pollingStation: '',
    languagePreference: 'ENGLISH',
    receiveSmsAlerts: true,
    receiveEmailAlerts: false,
  });
  
  // Location data states
  const [counties, setCounties] = useState<any[]>([]);
  const [constituencies, setConstituencies] = useState<any[]>([]);
  const [wards, setWards] = useState<any[]>([]);

  // Countdown timer for OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Load counties on mount when citizen tab is active
  useEffect(() => {
    if (activeTab === 'citizen') {
      loadCounties();
    }
  }, [activeTab]);

  // Load counties
  const loadCounties = async () => {
    try {
      const response = await apiService.getCounties();
      if (response.data.success) {
        setCounties(response.data.data || []);
      } else {
        setCounties([
          { id: 1, name: 'Nakuru County', code: '047', capital: 'Nakuru', governorName: 'Susan Kihika' },
          { id: 2, name: 'Nairobi County', code: '047', capital: 'Nairobi', governorName: 'Johnson Sakaja' },
        ]);
      }
    } catch (error) {
      console.error('Failed to load counties:', error);
      setCounties([
        { id: 1, name: 'Nakuru County', code: '047', capital: 'Nakuru', governorName: 'Susan Kihika' },
        { id: 2, name: 'Nairobi County', code: '047', capital: 'Nairobi', governorName: 'Johnson Sakaja' },
      ]);
    }
  };

  // Load constituencies when county is selected
  const loadConstituencies = async (countyId: string) => {
    if (!countyId) {
      setConstituencies([]);
      setWards([]);
      return;
    }
    
    try {
      const response = await apiService.getConstituenciesByCounty(parseInt(countyId));
      if (response.data.success) {
        setConstituencies(response.data.data || []);
        setWards([]);
        setCitizenRegisterData(prev => ({ ...prev, constituencyId: '', wardId: '' }));
      }
    } catch (error) {
      console.error('Failed to load constituencies:', error);
      if (countyId === '1') {
        setConstituencies([
          { id: 1, name: 'Nakuru Town East', countyId: 1, countyName: 'Nakuru County' },
          { id: 2, name: 'Nakuru Town West', countyId: 1, countyName: 'Nakuru County' },
          { id: 3, name: 'Naivasha', countyId: 1, countyName: 'Nakuru County' },
        ]);
      } else {
        setConstituencies([]);
      }
    }
  };

  // Load wards when constituency is selected
  const loadWards = async (constituencyId: string) => {
    if (!constituencyId) {
      setWards([]);
      return;
    }
    
    try {
      const response = await apiService.getWardsByConstituency(parseInt(constituencyId));
      if (response.data.success) {
        setWards(response.data.data || []);
        setCitizenRegisterData(prev => ({ ...prev, wardId: '' }));
      }
    } catch (error) {
      console.error('Failed to load wards:', error);
      if (constituencyId === '1') {
        setWards([
          { id: 1, name: 'Biashara Ward', constituencyId: 1, constituencyName: 'Nakuru Town East' },
          { id: 2, name: 'Kaptembwo Ward', constituencyId: 1, constituencyName: 'Nakuru Town East' },
          { id: 3, name: 'Rhoda Ward', constituencyId: 1, constituencyName: 'Nakuru Town East' },
        ]);
      } else {
        setWards([]);
      }
    }
  };

  // Handle location changes
  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setCitizenRegisterData(prev => {
      const updated = { ...prev, [name]: value };
      
      if (name === 'countyId') {
        updated.constituencyId = '';
        updated.wardId = '';
        setConstituencies([]);
        setWards([]);
        if (value) {
          loadConstituencies(value);
        }
      } else if (name === 'constituencyId') {
        updated.wardId = '';
        setWards([]);
        if (value) {
          loadWards(value);
        }
      }
      
      return updated;
    });
  };

  // Format phone number
  const formatPhoneNumber = (phone: string): string => {
    let cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = `254${cleaned.slice(1)}`;
    } else if (!cleaned.startsWith('254')) {
      cleaned = `254${cleaned}`;
    }
    return cleaned;
  };

  // Handle Admin Login
  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(adminCredentials);
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Aspirant OTP Request
  const handleAspirantOtpRequest = async () => {
    if (!aspirantCredentials.phoneNumber) {
      toast.error('Please enter phone number');
      return;
    }
    
    setLoading(true);
    setOtpError(null);
    try {
      await sendOtp(aspirantCredentials.phoneNumber);
      setOtpSent(true);
      toast.success('OTP sent successfully! Check your phone.', { duration: 5000, icon: '📱' });
      setCountdown(60);
      setOtpAttempts(0);
    } catch (error: any) {
      setOtpError(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Aspirant Login
  const handleAspirantLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setOtpError(null);
    
    try {
      await loginAspirant(aspirantCredentials.phoneNumber, aspirantCredentials.otp);
    } catch (error: any) {
      console.error('Aspirant login failed:', error);
      setOtpAttempts(prev => prev + 1);
      
      if (error.message?.toLowerCase().includes('otp') || error.message?.toLowerCase().includes('code')) {
        setOtpError('Invalid verification code. Please check and try again.');
        if (otpAttempts >= 2) {
          setAspirantCredentials(prev => ({ ...prev, otp: '' }));
          toast.error('Too many invalid attempts. Please request a new code.', { duration: 5000 });
        }
      } else {
        toast.error(error.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============ CITIZEN EMAIL/PASSWORD LOGIN ============
  
  const handleCitizenLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!citizenLoginData.email) {
      toast.error('Please enter your email address');
      return;
    }
    if (!citizenLoginData.password) {
      toast.error('Please enter your password');
      return;
    }
    
    setLoading(true);
    try {
      await loginCitizenWithEmailPassword(citizenLoginData.email, citizenLoginData.password);
    } catch (error: any) {
      console.error('Citizen login failed:', error);
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Forgot Password - Request OTP
  const handleForgotPasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordData.email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await citizenForgotPassword(forgotPasswordData.email);
      setResetOtpSent(true);
      setCountdown(60);
      toast.success('Reset code sent to your email!', { duration: 5000, icon: '📧' });
    } catch (error: any) {
      console.error('Forgot password request failed:', error);
      toast.error(error.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input for password reset
  const handleOtpFieldChange = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newFields = [...resetOtpFields];
      digits.forEach((digit, i) => { if (i < 6) newFields[i] = digit; });
      setResetOtpFields(newFields);
      setResetOtp(newFields.join(''));
      if (digits.length === 6) { document.getElementById(`reset-otp-5`)?.focus(); }
    } else if (/^\d*$/.test(value)) {
      const newFields = [...resetOtpFields];
      newFields[index] = value;
      setResetOtpFields(newFields);
      setResetOtp(newFields.join(''));
      if (value && index < 5) { document.getElementById(`reset-otp-${index + 1}`)?.focus(); }
    }
  };

  const handleResetOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !resetOtpFields[index] && index > 0) {
      document.getElementById(`reset-otp-${index - 1}`)?.focus();
    }
  };

  // Handle Password Reset
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (resetOtp.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    try {
      await citizenResetPassword(forgotPasswordData.email, resetOtp, newPassword);
      toast.success('Password reset successful! Please login with your new password.', { duration: 5000 });
      
      setCitizenStep('login');
      setResetOtpSent(false);
      setResetOtpFields(['', '', '', '', '', '']);
      setResetOtp('');
      setNewPassword('');
      setConfirmNewPassword('');
      setForgotPasswordData({ email: '' });
    } catch (error: any) {
      console.error('Password reset failed:', error);
      toast.error(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Citizen Registration
  const handleCitizenRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!citizenRegisterData.fullName) {
      toast.error('Full name is required');
      return;
    }
    if (!citizenRegisterData.email) {
      toast.error('Email address is required');
      return;
    }
    if (!citizenRegisterData.password) {
      toast.error('Password is required');
      return;
    }
    if (citizenRegisterData.password !== citizenRegisterData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (citizenRegisterData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    if (!citizenRegisterData.phoneNumber) {
      toast.error('Phone number is required');
      return;
    }
    if (!/^(0|\+?254)[0-9]{9}$/.test(citizenRegisterData.phoneNumber)) {
      toast.error('Please enter a valid Kenyan phone number (e.g., 0712345678 or +254712345678)', { duration: 4000 });
      return;
    }
    
    setLoading(true);
    try {
      const formattedPhone = formatPhoneNumber(citizenRegisterData.phoneNumber);
      
      const registerData = {
        email: citizenRegisterData.email.trim().toLowerCase(),
        password: citizenRegisterData.password,
        fullName: citizenRegisterData.fullName.trim(),
        phoneNumber: formattedPhone,
        idNumber: citizenRegisterData.idNumber?.trim() || undefined,
        gender: citizenRegisterData.gender || undefined,
        dateOfBirth: citizenRegisterData.dateOfBirth || undefined,
        countyId: citizenRegisterData.countyId ? parseInt(citizenRegisterData.countyId) : undefined,
        constituencyId: citizenRegisterData.constituencyId ? parseInt(citizenRegisterData.constituencyId) : undefined,
        wardId: citizenRegisterData.wardId ? parseInt(citizenRegisterData.wardId) : undefined,
        physicalAddress: citizenRegisterData.physicalAddress?.trim() || undefined,
        voterRegistrationNumber: citizenRegisterData.voterRegistrationNumber?.trim() || undefined,
        pollingStation: citizenRegisterData.pollingStation?.trim() || undefined,
        languagePreference: citizenRegisterData.languagePreference,
        receiveSmsAlerts: citizenRegisterData.receiveSmsAlerts,
        receiveEmailAlerts: citizenRegisterData.receiveEmailAlerts,
      };
      
      await citizenSignup(registerData);
      
      toast.success('Registration successful! Please login with your credentials.', { duration: 5000 });
      setCitizenStep('login');
      setCitizenLoginData({ email: citizenRegisterData.email, password: '' });
    } catch (error: any) {
      console.error('Citizen registration failed:', error);
      toast.error(error.message || 'Registration failed. Please check your information and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP for password reset
  const handleResendResetOtp = async () => {
    if (countdown > 0) {
      toast.error(`Please wait ${countdown} seconds before requesting a new code`);
      return;
    }
    setLoading(true);
    try {
      await citizenForgotPassword(forgotPasswordData.email);
      setCountdown(60);
      toast.success('New reset code sent to your email!', { duration: 5000, icon: '📧' });
    } catch (error: any) {
      toast.error(error.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fill demo data
  const fillDemoData = (type: 'admin' | 'aspirant' | 'citizen') => {
    if (type === 'admin') {
      setAdminCredentials({ email: 'admin@example.com', password: 'password123' });
      toast.success('Admin demo credentials filled');
    } else if (type === 'aspirant') {
      setAspirantCredentials({ phoneNumber: '0712345678', otp: '123456' });
      toast.success('Aspirant demo credentials filled');
    } else if (type === 'citizen') {
      if (citizenStep === 'login') {
        setCitizenLoginData({ email: 'john.citizen@example.com', password: 'password123' });
        toast.success('Citizen demo credentials filled');
      } else if (citizenStep === 'register') {
        setCitizenRegisterData({
          email: 'john.citizen@example.com',
          password: 'password123',
          confirmPassword: 'password123',
          fullName: 'John Citizen',
          phoneNumber: '0712345678',
          idNumber: '12345678',
          gender: 'MALE',
          dateOfBirth: '1990-01-01',
          countyId: '1',
          constituencyId: '1',
          wardId: '1',
          physicalAddress: '123 Main Street, Nakuru',
          voterRegistrationNumber: 'V123456789',
          pollingStation: 'Biashara Primary School',
          languagePreference: 'ENGLISH',
          receiveSmsAlerts: true,
          receiveEmailAlerts: true,
        });
        loadConstituencies('1');
        setTimeout(() => loadWards('1'), 100);
        toast.success('Citizen demo registration data filled');
      }
    }
  };

  // Go back handlers
  const handleBackToLogin = () => {
    setCitizenStep('login');
    setResetOtpSent(false);
    setResetOtpFields(['', '', '', '', '', '']);
    setResetOtp('');
    setNewPassword('');
    setConfirmNewPassword('');
    setForgotPasswordData({ email: '' });
  };

  const handleBackToForgotPassword = () => {
    setResetOtpSent(false);
    setResetOtpFields(['', '', '', '', '', '']);
    setResetOtp('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f4f7f6] to-[#e8f1ec] flex items-center justify-center p-4">
      <button onClick={() => navigate('/')} className="fixed top-4 left-4 z-10 flex items-center px-4 py-2 bg-white/90 backdrop-blur-sm text-[#0b2045] rounded-lg hover:bg-white hover:shadow-md transition-all duration-200 group">
        <ChevronLeft size={18} className="mr-1 group-hover:-translate-x-0.5 transition-transform" />
        <Home size={18} className="mr-2" />
        Back to Home
      </button>

      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="md:flex">
          {/* Left Side - Branding */}
          <div className="md:w-2/5 bg-gradient-to-br from-[#0b2045] to-[#1a3d6e] text-white relative overflow-hidden">
            <div className="absolute inset-0 z-0">
              <img src={COMMUNITY_IMAGE_URL} alt="Community Engagement" className="w-full h-full object-cover opacity-20" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#0b2045]/80 to-[#1a3d6e]/80"></div>
            </div>
            <div className="relative z-10 p-8 md:p-12 flex flex-col h-full">
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <Shield size={28} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Synchro Pamoja</h1>
                    <p className="text-[#a8d59b] text-sm">Community Engagement Platform</p>
                  </div>
                </div>
                <div className="my-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20">
                    <img src={COMMUNITY_IMAGE_URL} alt="Community members engaging" className="w-full h-48 object-cover" />
                    <div className="p-4">
                      <p className="text-sm text-[#a8d59b] italic">"Synchronizing communities for a better future"</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 mt-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm"><CheckCircle size={16} /></div>
                    <span className="text-[#a8d59b]">Report community issues instantly</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm"><CheckCircle size={16} /></div>
                    <span className="text-[#a8d59b]">Track issue resolution progress</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm"><CheckCircle size={16} /></div>
                    <span className="text-[#a8d59b]">Connect with local leaders</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm"><CheckCircle size={16} /></div>
                    <span className="text-[#a8d59b]">Stay updated on community news</span>
                  </div>
                </div>
              </div>
              <div className="mt-auto pt-8 border-t border-white/20">
                <p className="text-[#a8d59b] text-sm">Empowering citizens to actively participate in community development and governance.</p>
              </div>
            </div>
          </div>

          {/* Right Side - Login Forms */}
          <div className="md:w-3/5 p-8 md:p-12">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
              <p className="text-gray-600">Sign in to access your dashboard</p>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 mb-8">
              <button className={`flex-1 py-3 text-center font-medium transition-colors relative ${activeTab === 'admin' ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => { setActiveTab('admin'); setCitizenStep('login'); }}>
                <div className="flex items-center justify-center space-x-2"><Shield size={18} /><span>Admin/Staff</span></div>
              </button>
              <button className={`flex-1 py-3 text-center font-medium transition-colors ${activeTab === 'aspirant' ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => { setActiveTab('aspirant'); setCitizenStep('login'); setOtpSent(false); }}>
                <div className="flex items-center justify-center space-x-2"><User size={18} /><span>Aspirant</span></div>
              </button>
              <button className={`flex-1 py-3 text-center font-medium transition-colors ${activeTab === 'citizen' ? 'text-[#0b2045] border-b-2 border-[#3c8d2e]' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('citizen')}>
                <div className="flex items-center justify-center space-x-2"><Mail size={18} /><span>Citizen</span></div>
              </button>
            </div>

            {/* Admin Login */}
            {activeTab === 'admin' && (
              <form onSubmit={handleAdminLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input 
                      type="email" 
                      required 
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" 
                      placeholder="Enter your email" 
                      value={adminCredentials.email} 
                      onChange={(e) => setAdminCredentials({ ...adminCredentials, email: e.target.value })} 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input 
                      type="password" 
                      required 
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" 
                      placeholder="Enter your password" 
                      value={adminCredentials.password} 
                      onChange={(e) => setAdminCredentials({ ...adminCredentials, password: e.target.value })} 
                    />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 bg-[#3c8d2e] text-white font-medium rounded-lg hover:bg-[#2d7022] disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center">
                  {loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" />Signing in...</> : <>Sign In as Admin<ArrowRight className="ml-2" size={20} /></>}
                </button>
                <div className="text-center">
                  <button type="button" onClick={() => fillDemoData('admin')} className="text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:underline">
                  </button>
                </div>
              </form>
            )}

            {/* Aspirant Login - OTP */}
            {activeTab === 'aspirant' && (
              <form onSubmit={handleAspirantLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input type="tel" required className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="0712345678" value={aspirantCredentials.phoneNumber} onChange={(e) => setAspirantCredentials({ ...aspirantCredentials, phoneNumber: e.target.value })} />
                  </div>
                </div>
                {!otpSent ? (
                  <>
                    <button type="button" onClick={handleAspirantOtpRequest} disabled={loading} className="w-full py-3 bg-[#3c8d2e] text-white font-medium rounded-lg hover:bg-[#2d7022] disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center">
                      {loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" />Sending OTP...</> : <><Key className="mr-2" size={18} />Send Verification Code</>}
                    </button>
                    <div className="text-center">
                      <button type="button" onClick={() => fillDemoData('aspirant')} className="text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:underline">
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Verification Code</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input type="text" required maxLength={6} className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent ${otpError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="Enter 6-digit code" value={aspirantCredentials.otp} onChange={(e) => { setAspirantCredentials({ ...aspirantCredentials, otp: e.target.value.replace(/\D/g, '').slice(0, 6) }); setOtpError(null); }} />
                      </div>
                      {otpError && <div className="mt-2 flex items-center text-sm text-red-600"><AlertCircle size={14} className="mr-1" />{otpError}</div>}
                      <div className="mt-2 flex justify-between items-center text-sm">
                        <span className="text-gray-500 flex items-center"><RefreshCw size={14} className="mr-1" />Code expires in: {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}</span>
                        <button type="button" onClick={handleAspirantOtpRequest} disabled={countdown > 0 || loading} className="text-[#3c8d2e] hover:text-[#2d7022] disabled:text-gray-400 font-medium">{countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}</button>
                      </div>
                    </div>
                    <button type="submit" disabled={loading || aspirantCredentials.otp.length !== 6} className="w-full py-3 bg-[#3c8d2e] text-white font-medium rounded-lg hover:bg-[#2d7022] disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center">
                      {loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" />Verifying...</> : 'Verify & Sign In'}
                    </button>
                    <div className="text-center">
                      <button type="button" onClick={() => fillDemoData('aspirant')} className="text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:underline">
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}

            {/* Citizen Login/Registration - Email/Password */}
            {activeTab === 'citizen' && (
              <div>
                {/* Login Step */}
                {citizenStep === 'login' && (
                  <form onSubmit={handleCitizenLogin} className="space-y-6">
                    <div className="bg-[#e8f1ec] border border-[#3c8d2e]/30 rounded-lg p-4 mb-6 animate-fadeIn">
                      <div className="flex items-start"><Mail className="text-[#3c8d2e] mr-3 mt-0.5" size={20} /><div><h3 className="text-sm font-medium text-[#0b2045]">Citizen Access</h3><p className="text-sm text-[#0b2045]/80 mt-1">Sign in with your email and password to access citizen features.</p></div></div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <div className="relative"><Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="email" required className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="your.email@example.com" value={citizenLoginData.email} onChange={(e) => setCitizenLoginData({ ...citizenLoginData, email: e.target.value })} /></div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                      <div className="relative"><Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type={showPassword ? "text" : "password"} required className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="Enter your password" value={citizenLoginData.password} onChange={(e) => setCitizenLoginData({ ...citizenLoginData, password: e.target.value })} /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button></div>
                    </div>
                    <div className="flex justify-end"><button type="button" onClick={() => setCitizenStep('forgot-password')} className="text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:underline">Forgot Password?</button></div>
                    <button type="submit" disabled={loading} className="w-full py-3 bg-[#0b2045] text-white font-medium rounded-lg hover:bg-[#152c53] disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center">
                      {loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" />Signing In...</> : 'Sign In'}
                    </button>
                    <div className="text-center"><p className="text-sm text-gray-600">Don't have an account? <button type="button" onClick={() => setCitizenStep('register')} className="text-[#3c8d2e] hover:text-[#2d7022] font-medium hover:underline">Register here</button></p></div>
                    <div className="text-center">
                      <button type="button" onClick={() => fillDemoData('citizen')} className="text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:underline">
                       
                      </button>
                    </div>
                  </form>
                )}

                {/* Forgot Password Step */}
                {citizenStep === 'forgot-password' && !resetOtpSent && (
                  <form onSubmit={handleForgotPasswordRequest} className="space-y-6">
                    <div className="bg-[#fefce8] border border-[#ca8a04] rounded-lg p-4 mb-6 animate-fadeIn"><div className="flex items-start"><AlertCircle className="text-[#ca8a04] mr-3 mt-0.5" size={20} /><div><h3 className="text-sm font-medium text-[#854d0e]">Reset Password</h3><p className="text-sm text-[#854d0e]/80 mt-1">Enter your email address and we'll send you a verification code to reset your password.</p></div></div></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label><div className="relative"><Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="email" required className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="your.email@example.com" value={forgotPasswordData.email} onChange={(e) => setForgotPasswordData({ email: e.target.value })} /></div></div>
                    <div className="flex space-x-3"><button type="button" onClick={handleBackToLogin} className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"><ChevronLeft size={18} className="mr-1" />Back</button><button type="submit" disabled={loading} className="flex-1 py-3 bg-[#0b2045] text-white font-medium rounded-lg hover:bg-[#152c53] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center">{loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" />Sending...</> : 'Send Reset Code'}</button></div>
                  </form>
                )}

                {/* Reset Password with OTP Step */}
                {citizenStep === 'forgot-password' && resetOtpSent && (
                  <form onSubmit={handlePasswordReset} className="space-y-6">
                    <div className="bg-[#e8f1ec] border border-[#3c8d2e]/30 rounded-lg p-4 mb-6 animate-fadeIn"><div className="flex items-start"><CheckCircle className="text-[#3c8d2e] mr-3 mt-0.5" size={20} /><div><h3 className="text-sm font-medium text-[#0b2045]">Reset Password</h3><p className="text-sm text-[#0b2045]/80 mt-1">Enter the 6-digit code sent to <span className="font-medium">{forgotPasswordData.email}</span></p></div></div></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-3">Verification Code</label><div className="flex justify-between gap-2 mb-4">{[0, 1, 2, 3, 4, 5].map((index) => (<input key={index} id={`reset-otp-${index}`} type="text" maxLength={1} className="w-12 h-12 text-center text-xl font-semibold border rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent transition-all border-gray-300" value={resetOtpFields[index]} onChange={(e) => handleOtpFieldChange(index, e.target.value)} onKeyDown={(e) => handleResetOtpKeyDown(index, e)} inputMode="numeric" pattern="\d*" />))}</div><div className="mt-2 flex justify-between items-center text-sm"><span className="text-gray-500 flex items-center"><RefreshCw size={14} className="mr-1 animate-spin-slow" />Code expires in: {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}</span><button type="button" onClick={handleResendResetOtp} disabled={countdown > 0 || loading} className="text-[#3c8d2e] hover:text-[#2d7022] disabled:text-gray-400 font-medium transition-colors">{loading ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}</button></div></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-2">New Password</label><div className="relative"><Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type={showPassword ? "text" : "password"} required className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="Enter new password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button></div><p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters</p></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label><div className="relative"><Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type={showConfirmPassword ? "text" : "password"} required className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="Confirm new password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} /><button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">{showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button></div></div>
                    <div className="flex space-x-3"><button type="button" onClick={handleBackToForgotPassword} className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"><ChevronLeft size={18} className="mr-1" />Back</button><button type="submit" disabled={loading || resetOtp.length !== 6 || !newPassword || newPassword !== confirmNewPassword} className="flex-1 py-3 bg-[#0b2045] text-white font-medium rounded-lg hover:bg-[#152c53] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center">{loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" />Resetting...</> : 'Reset Password'}</button></div>
                    <div className="text-center"><button type="button" onClick={handleBackToLogin} className="text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:underline">Back to Login</button></div>
                  </form>
                )}

                {/* Registration Step */}
                {citizenStep === 'register' && (
                  <form onSubmit={handleCitizenRegister} className="space-y-6">
                    <div className="bg-[#e8f1ec] border border-[#3c8d2e]/30 rounded-lg p-4 mb-6 animate-fadeIn"><div className="flex items-start"><UserPlus className="text-[#3c8d2e] mr-3 mt-0.5" size={20} /><div><h3 className="text-sm font-medium text-[#0b2045]">New Citizen Registration</h3><p className="text-sm text-[#0b2045]/80 mt-1">Please complete your registration to access citizen features.</p></div></div></div>
                    
                    <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
                      {/* Account Information */}
                      <div className="bg-gray-50 p-4 rounded-lg"><h4 className="text-md font-medium text-gray-900 mb-4">Account Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label><div className="relative"><Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="email" required className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="your.email@example.com" value={citizenRegisterData.email} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, email: e.target.value})} /></div></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label><div className="relative"><Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="tel" required className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="0712345678" value={citizenRegisterData.phoneNumber} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, phoneNumber: e.target.value})} /></div></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Password *</label><div className="relative"><Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type={showPassword ? "text" : "password"} required className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="Create a password" value={citizenRegisterData.password} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, password: e.target.value})} /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button></div></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label><div className="relative"><Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type={showConfirmPassword ? "text" : "password"} required className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="Confirm your password" value={citizenRegisterData.confirmPassword} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, confirmPassword: e.target.value})} /><button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">{showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button></div></div>
                        </div>
                      </div>

                      {/* Personal Information */}
                      <div className="bg-gray-50 p-4 rounded-lg"><h4 className="text-md font-medium text-gray-900 mb-4">Personal Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label><div className="relative"><User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="text" required className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="John Citizen" value={citizenRegisterData.fullName} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, fullName: e.target.value})} /></div></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">ID Number</label><div className="relative"><ShieldCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="text" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="12345678" value={citizenRegisterData.idNumber} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, idNumber: e.target.value})} /></div></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Gender</label><select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" value={citizenRegisterData.gender} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, gender: e.target.value})}><option value="">Select Gender</option><option value="MALE">Male</option><option value="FEMALE">Female</option><option value="OTHER">Other</option></select></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label><div className="relative"><Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="date" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" value={citizenRegisterData.dateOfBirth} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, dateOfBirth: e.target.value})} /></div></div>
                        </div>
                      </div>

                      {/* Location Information */}
                      <div className="bg-gray-50 p-4 rounded-lg"><h4 className="text-md font-medium text-gray-900 mb-4">Location Information</h4>
                        <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-2">County</label><div className="relative"><Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><select name="countyId" value={citizenRegisterData.countyId} onChange={handleLocationChange} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"><option value="">Select County</option>{counties.map((county) => (<option key={county.id} value={county.id}>{county.name}</option>))}</select></div></div>
                        <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-2">Constituency</label><div className="relative"><Map className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><select name="constituencyId" value={citizenRegisterData.constituencyId} onChange={handleLocationChange} disabled={!citizenRegisterData.countyId} className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent ${!citizenRegisterData.countyId ? 'bg-gray-100 cursor-not-allowed' : ''}`}><option value="">Select Constituency</option>{constituencies.map((constituency) => (<option key={constituency.id} value={constituency.id}>{constituency.name}</option>))}</select></div></div>
                        <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-2">Ward</label><div className="relative"><MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><select name="wardId" value={citizenRegisterData.wardId} onChange={handleLocationChange} disabled={!citizenRegisterData.constituencyId} className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent ${!citizenRegisterData.constituencyId ? 'bg-gray-100 cursor-not-allowed' : ''}`}><option value="">Select Ward</option>{wards.map((ward) => (<option key={ward.id} value={ward.id}>{ward.name}</option>))}</select></div></div>
                        <div><label className="block text-sm font-medium text-gray-700 mb-2">Physical Address</label><textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" rows={2} placeholder="Street, City, County" value={citizenRegisterData.physicalAddress} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, physicalAddress: e.target.value})} /></div>
                      </div>

                      {/* Additional Information */}
                      <div className="bg-gray-50 p-4 rounded-lg"><h4 className="text-md font-medium text-gray-900 mb-4">Additional Information (optional)</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Voter Registration Number</label><input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="V123456789" value={citizenRegisterData.voterRegistrationNumber} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, voterRegistrationNumber: e.target.value})} /></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Polling Station</label><input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" placeholder="Biashara Primary School" value={citizenRegisterData.pollingStation} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, pollingStation: e.target.value})} /></div>
                        </div>
                        <div className="mt-4"><label className="block text-sm font-medium text-gray-700 mb-2">Language Preference</label><div className="relative"><Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><select className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" value={citizenRegisterData.languagePreference} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, languagePreference: e.target.value})}><option value="ENGLISH">English</option><option value="SWAHILI">Swahili</option><option value="BILINGUAL">Bilingual</option></select></div></div>
                        <div className="mt-4 flex items-center space-x-6"><div className="flex items-center"><input type="checkbox" id="smsAlerts" checked={citizenRegisterData.receiveSmsAlerts} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, receiveSmsAlerts: e.target.checked})} className="h-4 w-4 text-[#3c8d2e] rounded focus:ring-[#3c8d2e]" /><label htmlFor="smsAlerts" className="ml-2 text-sm text-gray-700">Receive SMS Alerts</label></div><div className="flex items-center"><input type="checkbox" id="emailAlerts" checked={citizenRegisterData.receiveEmailAlerts} onChange={(e) => setCitizenRegisterData({...citizenRegisterData, receiveEmailAlerts: e.target.checked})} className="h-4 w-4 text-[#3c8d2e] rounded focus:ring-[#3c8d2e]" /><label htmlFor="emailAlerts" className="ml-2 text-sm text-gray-700">Receive Email Alerts</label></div></div>
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-4"><button type="button" onClick={() => setCitizenStep('login')} className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"><ChevronLeft size={18} className="mr-1" />Cancel</button><button type="submit" disabled={loading || !citizenRegisterData.fullName || !citizenRegisterData.email || !citizenRegisterData.password} className="flex-1 py-3 bg-[#0b2045] text-white font-medium rounded-lg hover:bg-[#152c53] disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center">{loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" />Registering...</> : 'Register'}</button></div>
                    <div className="text-center"><button type="button" onClick={() => fillDemoData('citizen')} className="text-sm text-[#3c8d2e] hover:text-[#2d7022] hover:underline"></button></div>
                  </form>
                )}
              </div>
            )}

            {/* Help Text */}
            <div className="mt-8 pt-6 border-t border-gray-200"><p className="text-center text-sm text-gray-500">{activeTab === 'admin' && 'Administrator and staff access for managing the platform'}{activeTab === 'aspirant' && 'For political aspirants and candidates to manage campaigns'}{activeTab === 'citizen' && 'For community members to report issues and track progress'}</p></div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); } 20%, 40%, 60%, 80% { transform: translateX(2px); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-shake { animation: shake 0.5s ease-in-out; }
        .animate-spin-slow { animation: spin 3s linear infinite; }
      `}</style>
    </div>
  );
};

export default Login;